<script setup lang="ts">
import debounce from 'lodash/debounce'
import moment from 'moment'
import { useuseCalendarioStore } from './store/useCalendarioStore'

definePage({
  meta: {
    layout: 'landing-page',
    public: true,
  },
})

const store = useuseCalendarioStore()

const {
  search,
  eventsInfinite,
  events,
  eventsTotal,
  loading,
} = storeToRefs(store)

const {
  fetchEventsCalendar,
} = store

onMounted(() => {
  fetchEventsCalendar({ done: () => {} })
})

const onSearch = debounce(value => {
  events.value = []
  fetchEventsCalendar({ done: () => {} })
}, 300)
</script>

<template>
  <VContainer
    fluid
    class="calendar-bg py-8"
  >
    <VCard class="calendar-card mx-auto pa-2 pa-md-8 h-screen h-sm-auto">
      <VCardText>
        <div class="d-flex flex-column align-center mb-2 mb-md-6">
          <h1 class="calendar-title mb-2">
            Calendário de Eventos
          </h1>
          <p class="calendar-subtitle">
            Encontre eventos relevantes para o seu negócio.
          </p>
        </div>

        <div class="d-flex flex-row flex-wrap gap-4 mb-6 justify-center align-center">
          <div class="d-flex flex-column w-100 w-sm-auto">
            <VLabel>Pesquisar:</VLabel>
            <VTextField
              v-model.lazy="search.q"
              placeholder="Pesquise por nome ou descrição"
              class="search-field"
              hide-details
              clearable
              prepend-inner-icon="tabler-search"
              :style="{
                width: $vuetify.display.smAndDown ? '100%' : $vuetify.display.mdAndUp ? '300px' : '500px',
              }"
              @update:model-value="onSearch"
            />
          </div>
          <div class="d-flex flex-column w-100 w-sm-auto">
            <VLabel>Data do Evento:</VLabel>
            <AppTextField
              v-model="search.date"
              type="date"
              class="search-field"
              hide-details
              clearable
              :style="{
                width: $vuetify.display.smAndDown ? '100%' : '200px',
              }"
              @update:model-value="onSearch"
            />
          </div>
        </div>

        <div class="featured-companies">
          <VInfiniteScroll
            v-if="events.length > 0"
            ref="eventsInfinite"
            color="secondary"
            mode="manual"
            class="supplier-content flex-row flex-wrap my-2"
            @load="fetchEventsCalendar"
          >
            <VCol
              v-for="(card, index) in events"
              :key="index"
              class="d-flex supplier-card"
              cols="12"
              md="6"
            >
              <VCard
                class="event-card d-flex w-100 elevation-4"
                hover
              >
                <VRow class="ma-0">
                  <VCol
                    class="d-flex pa-0"
                    cols="5"
                  >
                    <VImg
                      :src="card.banner ?? 'https://placehold.co/1920x1080'"
                      eager
                      cover
                      class="card-image supplier-image"
                      max-height="200"
                    />
                  </VCol>
                  <VCol class="d-flex">
                    <div class="supplier-content d-flex flex-column">
                      <h3 class="event-title mb-2">
                        {{ card.nome }}
                      </h3>
                      <p class="event-desc ma-0 mb-2">
                        {{ `${card.descricao?.slice(0, 300)}…` }}
                      </p>
                      <VSpacer />
                      <div class="d-flex flex-row align-center gap-2">
                        <VIcon
                          color="primary"
                          icon="tabler-map-pin"
                        />
                        <span class="event-location">{{ card.local_evento }}</span>
                      </div>
                      <div class="d-flex flex-row align-center gap-2 mt-1">
                        <VIcon
                          color="primary"
                          icon="tabler-calendar"
                        />
                        <span class="event-date">{{ moment(card.data).format('dddd, D [de] MMMM [às] HH:mm') }}</span>
                      </div>
                    </div>
                  </VCol>
                </VRow>
              </VCard>
            </VCol>

            <template #load-more="{ props }">
              <VBtn
                v-if="events.length !== eventsTotal"
                v-bind="props"
                class="load-more-btn"
                width="170"
                size="large"
              >
                Ver mais
              </VBtn>
            </template>
            <template #empty />
          </VInfiniteScroll>

          <VEmptyState
            v-else
            class="supplier-empty"
            icon="tabler-search"
            title="Nenhum resultado encontrado"
            :size="$vuetify.display.smAndDown ? '50' : '80'"
            color="primary"
            subtitle="Tente novamente com outras palavras-chave."
          />
        </div>
      </VCardText>
    </VCard>
  </VContainer>
</template>

<style lang="scss">
.calendar-bg {
  background: linear-gradient(135deg, #e0e7ff 0%, #f8fafc 100%);
  min-block-size: 100vh;
}

.calendar-card {
  border-radius: 24px;
  background: rgba(255, 255, 255, 95%);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 15%);
}

.calendar-title {
  background: linear-gradient(90deg, #3b82f6 0%, #2BB6FC 100%);
  background-clip: text;
  font-weight: 800;
  line-height: 1;
  text-align: center;
  -webkit-text-fill-color: transparent;
}

.calendar-subtitle {
  color: #64748b;
  font-weight: 500;
}

.search-field {
  border-radius: 12px !important;
  background: #f1f5f9;
  box-shadow: 0 2px 8px 0 rgba(59, 130, 246, 8%);
}

.supplier-content {
  .v-infinite-scroll__side {
    display: none;
  }
}

.supplier-empty {
  block-size: calc(100% - 25%);
}

.supplier-card {
  display: flex;
  flex-direction: row;
  column-gap: 20px;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);

  .event-card {
    border-radius: 18px;
    box-shadow: 0 2px 12px 0 rgba(59, 130, 246, 10%);
    transition: box-shadow 0.3s, transform 0.3s;

    &:hover {
      box-shadow: 0 8px 32px 0 rgba(99, 102, 241, 18%);
      transform: translateY(-6px) scale(1.03);
    }
  }

  .supplier-image {
    img {
      border-radius: 18px 0 0 18px;
      transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);

      &:hover {
        transform: scale(1.08) rotate(-2deg);
      }
    }
  }

  .supplier-content {
    flex: 1;
    padding-block: 12px;
    padding-inline: 12px 0;
  }

  .event-title {
    color: #3b82f6;
    font-size: 1rem;
    font-weight: 700;
  }

  .event-desc {
    color: #334155;
    font-size: 0.8rem;
    font-weight: 400;
  }

  .event-location,
  .event-date {
    color: #2BB6FC;
    font-size: 0.8rem;
    font-weight: 600;
  }
}

.load-more-btn {
  border-radius: 12px;
  background: linear-gradient(90deg, #3b82f6 0%, #2BB6FC 100%);
  box-shadow: 0 2px 8px 0 rgba(99, 102, 241, 12%);
  color: #fff !important;
  font-weight: 700;
  transition: background 0.3s, box-shadow 0.3s;

  &:hover {
    background: linear-gradient(90deg, #2BB6FC 0%, #3b82f6 100%);
    box-shadow: 0 8px 24px 0 rgba(59, 130, 246, 18%);
  }
}
</style>
